import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      const from = location.state?.from?.pathname || "/app/home";
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // Emergent's managed OAuth only allows redirects to *.emergent.host / *.emergentagent.com.
    // For custom domains, we go through the OAuth bridge page hosted on the .emergent.host URL,
    // which then forwards the session_id back to the custom domain.
    const origin = window.location.origin;
    const isWhitelistedOrigin =
      /\.emergent\.host$/i.test(origin) ||
      /\.emergentagent\.com$/i.test(origin) ||
      /^http:\/\/localhost(:\d+)?$/i.test(origin);

    let redirectUrl;
    if (isWhitelistedOrigin) {
      redirectUrl = origin + "/app/home";
    } else {
      const bridgeHost = process.env.REACT_APP_EMERGENT_HOST || "https://ai-notetaker-3.emergent.host";
      const returnTo = encodeURIComponent(origin + "/app/home");
      redirectUrl = `${bridgeHost}/auth/oauth-bridge?return_to=${returnTo}`;
    }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden md:flex flex-col justify-between bg-stone-900 p-12 text-stone-50 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/3bfad991-7fc9-4c85-b4b7-558ca5444229/images/055f79dad6fd5ffb9bcbe510091d1f25143d28cc700b14ce43ac17cd5fb96f42.png"
            alt="abstract"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative">
          <Link to="/"><Logo /></Link>
        </div>
        <div className="relative max-w-md">
          <p className="font-display text-3xl font-medium tracking-tight leading-snug">
            "I stopped writing meeting notes a month ago. My calendar feels lighter and my action items
            actually get done."
          </p>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-stone-400">
            ROHAN MEHTA · DIRECTOR OF PRODUCT
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8"><Link to="/"><Logo /></Link></div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to your Noteology workspace</p>

          <Button
            onClick={handleGoogle}
            variant="outline"
            className="mt-8 w-full rounded-lg py-6 text-base"
            data-testid="google-signin-button"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">OR</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full rounded-lg py-6" disabled={loading} data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            New to Noteology? <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="login-signup-link">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
